import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Heart, FileText, MessageCircle, Instagram, Facebook, Linkedin, Zap } from 'lucide-react';
import HourglassLoader from './ui/HourglassLoader';
import apiClient from '../lib/apiClient';
import storage from '../lib/storage';
import { energyService } from '../services';
import Tooltip from '../ui/Tooltip/Tooltip';
import './Dashboard.css';

function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodFilter, setPeriodFilter] = useState('30d');

  const loadAll = async () => {
    setLoading(true);
    try {
      const token = storage.get('authToken');
      const sessionId = storage.get('guest_session_id');
      const path = token ? '/dashboard/metrics' : `/dashboard/metrics?session_id=${sessionId || ''}`;
      const [metricsData, energyData] = await Promise.all([
        apiClient.request(path, { auth: Boolean(token) }),
        token ? energyService.getEnergy() : Promise.resolve(null)
      ]);

      if (metricsData?.success) setMetrics(metricsData.metrics || null);
      if (energyData?.success) setEnergy(energyData || null);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const cards = useMemo(() => {
    if (!metrics) return [];
    return [
      { icon: FileText, label: 'Posts Gerados', value: metrics.total_posts || 0 },
      { icon: Heart, label: 'Conteúdos Salvos', value: metrics.total_contents || 0 },
      { icon: MessageCircle, label: 'Interações IA', value: metrics.total_interactions || 0 }
    ];
  }, [metrics]);

  const platforms = useMemo(() => {
    if (!metrics) return [];
    return [
      { icon: Instagram, label: 'Instagram', value: metrics.posts_by_platform?.instagram || 0 },
      { icon: Facebook, label: 'Facebook', value: metrics.posts_by_platform?.facebook || 0 },
      { icon: Linkedin, label: 'LinkedIn', value: metrics.posts_by_platform?.linkedin || 0 }
    ];
  }, [metrics]);

  return (
    <div className="contents-page">
      <div className="contents-header">
        <button className="btn btn-outline" onClick={loadAll}>
          <div className="blob1"></div>
          <div className="blob2"></div>
          <div className="inner">
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Atualizar
          </div>
        </button>
      </div>

      {loading && <HourglassLoader variant="inline" message="Carregando métricas..." />}

      {!loading && (
        <div className="contents-layout">
          <main className="contents-primary">
            {energy && (
              <section className="card dashboard-energy-card">
                <div className="card-header">
                  <h3><Zap size={18} /> Energia</h3>
                  <span className="badge badge-success">{energy.level || 'Iniciante'}</span>
                </div>
                <p>{energy.energy || 0} créditos disponíveis</p>
                <p>Código de referral: {energy.referral_code || '---'}</p>
              </section>
            )}

            <section className="contents-grid">
              {cards.map((card) => {
                const Icon = card.icon;
                return (
                  <Tooltip
                    key={card.label}
                    content="Este card mostra métricas essenciais para validar evolução de produção e engajamento."
                    variant="tip"
                    block
                  >
                    <article className="content-card card">
                      <div className="content-info">
                        <h3>{card.label}</h3>
                        <p className="content-engagement">{card.value}</p>
                        <Icon size={18} />
                      </div>
                    </article>
                  </Tooltip>
                );
              })}
            </section>
          </main>

          <aside className="contents-secondary">
            <section className="card contents-filters">
              <Tooltip content="Use os filtros para analisar o desempenho em diferentes períodos de conteúdo." variant="info" block>
                <div className="contents-toolbar">
                  <select value={periodFilter} onChange={(event) => setPeriodFilter(event.target.value)}>
                    <option value="7d">Últimos 7 dias</option>
                    <option value="30d">Últimos 30 dias</option>
                    <option value="90d">Últimos 90 dias</option>
                  </select>
                </div>
              </Tooltip>

              <Tooltip content="Resumo visual por plataforma para comparar volume de conteúdo publicado." variant="tip" block>
                <h3 className="contents-filters-title">Plataformas</h3>
              </Tooltip>
              <div className="contents-toolbar">
                {platforms.map((platform) => {
                  const Icon = platform.icon;
                  return (
                    <Tooltip key={platform.label} content={`Gráfico resumido de ${platform.label} para acompanhamento rápido.`} variant="info" block>
                      <div className="search-box">
                        <Icon size={16} />
                        <span>{platform.label}</span>
                        <strong>{platform.value}</strong>
                      </div>
                    </Tooltip>
                  );
                })}
              </div>
            </section>
          </aside>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
